import {observable, action, runInAction, computed} from 'mobx';
import PolicyWagePrelimInfoService from '~/core/services/PolicyWagePrelimInfoService';
import PolicyWageDeclarationService from '~/core/services/PolicyWageDeclarationService';
import {parseJSON} from '~/core/utils/JsonUtility';
import AsyncStatus from '~/core/models/AsyncStatus';
import workerTypes from '~/core/models/enums/WorkerTypes';
import {PrelimInfoFactory} from '../Models/PolicyAuth';
import {PolicyFactory} from '../Models/PolicyModel';
import {AsbestosFactory} from '../Models/Asbestos';
import {ContractorWagesFactory} from '../Models/ContractorWages';
import {TaxiFactory} from '../Models/TaxiModel';
import {SubmitterFactory} from '../Models/SubmitterInfo';

export default class PolicyWageDeclarationStore {
	//region Properties

	/**
	 * Data item
	 * @type {?type}
	 */
	@observable preliminaryInfo = null;
	@observable submitterInfo = {
		companyName: null,
		contactNumber: null,
		emailAddress: null,
		firstName: null,
		lastName: null,
		position: null,
		submitterType: null,
	};
	@observable preliminaryModel = null;
	@observable policyModel = null;
	@observable wageDeclarationActivities = [];
	@observable premiumInfo = {
		previousPremium: 0,
		revisedPremium: 0,
		changeInCost: 0,
		error: 'false',
	};
	@observable retryAttempts = 0;
	@observable isRedirectWithError = true;
	@observable hasDuplicateTransaction = false;
	@observable formObjs = [];
	@observable userAction = {
		ADD: 0,
		EDIT: 0,
	};
	/**
	 * Sync status of retrieving the reference data via network
	 * @type {AsyncStatus}
	 */
	@observable asyncStatus = new AsyncStatus();
	//endregion

	//region Lifecycle

	/**
	 * Constructor
	 * @param rootStore
	 */
	constructor(rootStore = null) {
		this.rootStore = rootStore;
	}

	@computed
	get totalWages() {
		return this.policyModel && this.policyModel.totalWages;
	}

	@computed
	get totalEmployees() {
		return this.policyModel && this.policyModel.totalEmployees;
	}

	getUnitsorPlates(wagesItem) {
		let Units = 0;

		if (!wagesItem.isTaxi) {
			Units =
				wagesItem.unitCount != null && wagesItem !== undefined && wagesItem.unitCount != null
					? parseInt(wagesItem.unitCount, 10)
					: 0;
		} else {
			Units =
				wagesItem.platesCount != null && wagesItem !== undefined && wagesItem.platesCount != null
					? parseInt(wagesItem.platesCount, 10)
					: 0;
		}

		return Units;
	}

	@computed
	get totalUnits() {
		return this.wageDeclarationActivities
			.filter(wagesItem => wagesItem.isPercapita)
			.map(wagesItem => this.getUnitsorPlates(wagesItem))
			.reduce((a, b) => a + b, 0);
	}

	@action
	deletewWageActivity(wageID) {
		this.policyModel.wics.find(o => {
			if (o.id === wageID) {
				o.isRemoved = true;
			}
		});
		if (this.policyModel && this.policyModel.wics.length) {
			const removedPolicy = this.policyModel.wics.filter(policy => policy.isRemoved);
			this.policyModel.submitWics.push(removedPolicy[0]);

			this.policyModel.wics = this.policyModel.wics.filter(policy => !policy.isRemoved);
			this.getWageActivities();
		}
	}

	@action
	pushNewWageActivity(wage) {
		if (this.policyModel && wage) {
			this.policyModel.wics.push(wage);
			this.getWageActivities();
		}
	}

	@action
	replaceWageActivity(wage, index) {
		if (this.policyModel && wage) {
			this.policyModel.wics[index] = wage;
			this.getWageActivities();
		}
	}

	@action
	updateNewPercapitaWageActivity(wage, formData) {
		for (const key in formData) {
			if (formData.hasOwnProperty(key)) {
				const fieldValue = formData[key];
				if (key.includes('formSection')) {
					wage.setBusinessDescription(fieldValue.businessDescription);
					if (wage.isTaxi) {
						if (fieldValue.plateNumberBlock) {
							const plates = fieldValue.plateNumberBlock.map(TaxiFactory.createFromFormObject);
							wage.setTaxiModel(plates);
						}
					} else {
						wage.setUnits(fieldValue.unitCount);
					}
					break;
				}
			}
		}
	}

	@action
	updateNewWageActivity(wage, formData) {
		const formSectionKey = Object.keys(formData).find(key => key.includes('formSection'));
		if (formSectionKey) {
			const fieldValue = formData[formSectionKey];
			wage.setBusinessDescription(fieldValue.businessDescription);
			wage.setEmployees(fieldValue.employees);
			wage.setWages(fieldValue.wages);
			wage.setHasApprentices(fieldValue.workers.includes(workerTypes.APPRENTICES));
			wage.setHasAsbestos(fieldValue.workers.includes(workerTypes.ASBESTOS_WORKER));
			wage.setHasContractors(fieldValue.workers.includes(workerTypes.CONTRACTORS));
		}

		for (const key in formData) {
			if (formData.hasOwnProperty(key)) {
				const fieldValue = formData[key];
				if (key.includes('apprentice') && formData && fieldValue) {
					if (wage.hasApprentices) {
						wage.setApprenticeNumber(fieldValue.apprenticeNumber);
						wage.setApprenticeWages(fieldValue.apprenticeWages);
						wage.setNationalQualificationCode(fieldValue.nationalQualificationCode);
					} else {
						wage.setApprenticeNumber(null);
						wage.setApprenticeWages(null);
						wage.setNationalQualificationCode(null);
					}
				} else if (key.includes('asbestos') && formData && fieldValue) {
					if (wage.hasAsbestos) {
						wage.setAsbestosObject([AsbestosFactory.createFromServiceObject(fieldValue)]);
					} else {
						wage.setAsbestosObject([]);
					}
				} else if (key.includes('contractor')) {
					if (wage.hasContractors) {
						wage.setContractorWages(
							fieldValue.contractorWage.map(value => ContractorWagesFactory.createFromServiceObject(value)),
						);
					} else {
						wage.setContractorWages([]);
					}
				}
			}
		}
	}

	/**
	 * Action
	 */
	@action
	getWageActivities() {
		this.wageDeclarationActivities = this.policyModel.wics.filter(policy => !policy.isRemoved);
		return this.wageDeclarationActivities;
	}

	@action
	setPreliminaryInfo(preliminaryInfo = null) {
		this.preliminaryInfo = preliminaryInfo;
		this.preliminaryModel = PrelimInfoFactory.createFromFormObject(this.preliminaryInfo);
	}

	@action
	async submitRequest(token, clientid, clientSecret) {
		try {
			this.asyncStatus.isBusy = true;
			this.asyncStatus.error = null;
			const resp = await PolicyWagePrelimInfoService.submitRequest(
				this.preliminaryModel,
				token,
				clientid,
				clientSecret,
			);
			runInAction(() => {
				this.setPolicy(parseJSON(resp));
				this.asyncStatus.isBusy = false;
				this.asyncStatus.isSuccess = true;
				this.asyncStatus.error = null;
				this.destroyPrelimInfoPolicy();
			});
		} catch (e) {
			runInAction(() => {
				this.destroyPrelimInfoPolicy();
				this.asyncStatus.error = this.validateError(e);
				this.asyncStatus.isBusy = false;
			});
		}
	}

	@action
	async submitWageRequest(clientID, clientSecret, token) {
		try {
			this.asyncStatus.isBusy = true;
			this.asyncStatus.error = null;
			this.isRedirectWithError = false;
			const resp = await PolicyWageDeclarationService.submitRequest(this.policyModel, clientID, clientSecret, token);
			runInAction(() => {
				this.validateUnderwritingError(parseJSON(resp));
				this.asyncStatus.isBusy = false;
				this.asyncStatus.isSuccess = true;
				this.asyncStatus.error = null;
			});
		} catch (e) {
			runInAction(() => {
				this.asyncStatus.error = this.validateError(e);
				this.validateAndRedirect(e);
				this.asyncStatus.isBusy = false;
				this.asyncStatus.isSuccess = e.response.body.errors[0].code === '1501';
			});
		}
	}

	// @action
	validateUnderwritingError(response) {
		if (response && response.data && response.data.attributes) {
			if (response.data.attributes.IsUnderWriterError === 'true' || response.data.attributes.IsUnderWriterError) {
				this.premiumInfo.previousPremium = 0;
				this.premiumInfo.revisedPremium = 0;
				this.premiumInfo.changeInCost = 0;
				this.premiumInfo.error = 'true';
			} else if (
				response.data.attributes.premiumDetails &&
				(response.data.attributes.IsUnderWriterError === 'false' || !response.data.attributes.IsUnderWriterError)
			) {
				this.premiumInfo.previousPremium = response.data.attributes.premiumDetails.priorPremium;
				this.premiumInfo.revisedPremium = response.data.attributes.premiumDetails.hindsightPremium;
				this.premiumInfo.changeInCost = response.data.attributes.premiumDetails.changeInCost;
				this.premiumInfo.error = 'false';
			}

			if (response.data.attributes.hasValidationErrors === 'true' || response.data.attributes.hasValidationErrors) {
				this.premiumInfo.previousPremium = 0;
				this.premiumInfo.revisedPremium = 0;
				this.premiumInfo.changeInCost = 0;
				this.premiumInfo.error = 'true';
			}
		} else {
			this.premiumInfo.previousPremium = 0;
			this.premiumInfo.revisedPremium = 0;
			this.premiumInfo.changeInCost = 0;
			this.premiumInfo.error = 'true';
		}
	}

	// @action
	validateError(responseError) {
		if (
			responseError &&
			responseError.response &&
			responseError.response.body &&
			responseError.response.body.errors &&
			responseError.response.body.errors.length > 0
		) {
			if (
				responseError.response.body.errors[0].status === '404' ||
				responseError.response.body.errors[0].code === '1350'
			) {
				this.retryAttempts = this.retryAttempts + 1;
				if (this.retryAttempts <= 3) {
					responseError.response.body.errors[0].code = 'retry_attempt_not_exceeded';
				} else if (this.retryAttempts > 3) {
					responseError.response.body.errors[0].code = 'retry_attempt_exceeded';
				}
			}
		}
		return responseError;
	}

	//@action
	validateAndRedirect(responseError) {
		if (
			responseError &&
			responseError.response &&
			responseError.response.body &&
			responseError.response.body.errors &&
			responseError.response.body.errors.length > 0
		) {
			this.isRedirectWithError = false;
			this.hasDuplicateTransaction = false;
			if (
				responseError.response.body.errors[0].code === '600' ||
				responseError.response.body.errors[0].code === '605'
			) {
				this.isRedirectWithError = true;
				this.hasDuplicateTransaction = false;
			}
			if (responseError.response.body.errors[0].code === '1501') {
				this.hasDuplicateTransaction = true;
				this.isRedirectWithError = false;
			}
		}
	}

	@action
	setPolicy(resp) {
		if (resp && resp.data) {
			this.policyModel = PolicyFactory.createFromService(resp.data);
			if (this.policyModel) {
				this.policyModel.wics = this.policyModel.wics.filter(policy => !policy.isRemoved);
				this.getWageActivities();
			}
		}
	}

	@action
	destroyPrelimInfoPolicy() {
		this.preliminaryModel = null;
	}

	@action
	destroyPolicy() {
		this.policyModel = null;
		this.preliminaryModel = null;
		this.wageDeclarationActivities = [];
		this.premiumInfo = null;
		this.retryAttempts = 0;
		this.formObjs = [];
	}

	@action
	getPreliminaryInfo() {
		return this.preliminaryInfo;
	}

	@action
	_setErrorMessage = msg => {
		this.asyncStatus.isBusy = false;
		this.asyncStatus.isSuccess = false;
		this.asyncStatus.error = typeof msg === 'string' ? msg : msg.toString();
	};

	@action
	_setSuccessMessage = jsonResponse => {
		this.asyncStatus.isBusy = false;
		this.asyncStatus.isSuccess = true;
		this.asyncStatus.error = '';
		this.referenceNumber = jsonResponse.id;
	};

	@action
	assignFormObjects(id, form) {
		if (this.policyModel && this.policyModel.wics) {
			this.policyModel.wics.find((o, i) => {
				if (o.id === id) {
					this.policyModel.wics[i].FormObject = form;
				}
			});
		}
	}

	@action
	getPolicyModelWics() {
		if (this.policyModel && this.policyModel.wics) {
			this.policyModel.wics = this.policyModel.wics.filter(policy => !policy.isRemoved);
			return this.policyModel.wics;
		}
		return null;
	}

	@action
	setSubmitterInfo(companyName, contactNumber, emailAddress, firstName, lastName, position, submitterType) {
		this.submitterInfo.companyName = companyName;
		this.submitterInfo.contactNumber = contactNumber;
		this.submitterInfo.emailAddress = emailAddress;
		this.submitterInfo.firstName = firstName;
		this.submitterInfo.lastName = lastName;
		this.submitterInfo.position = position;
		this.submitterInfo.submitterType = submitterType === 'on_behalf' ? false : true;
		const submitterInformation = SubmitterFactory.createFromService(this.submitterInfo);
		this.policyModel.SubmitterModel = submitterInformation;
	}

	//endregion
}
